import { useState, useEffect, useMemo, useRef } from "react";

import {
  Box,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CurrencyLiraIcon from "@mui/icons-material/CurrencyLira";
import { DataGrid } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";

import { getCurrentTenant } from "../../config/tenantConfig";
import ConvertOIDValueToString from "../../utils/objectConverter";
import { getAggregation } from "../../api/aggregationAPI";
import {getProjects} from "../../api/projectAPI";
import { usePageVisibility } from "../../hooks/usePageVisibility";
import { useObjectData } from "../../hooks/useLocalStorageData";

const { apiBaseUrl: API_BASE_URL } = getCurrentTenant();

function WidgetBox({ widget, getAggregation, objectData }) {
  const { t } = useTranslation();
  const [value, setValue] = useState(null);
  const [loadingValue, setLoadingValue] = useState(true);
  const isPageVisible = usePageVisibility();

  useEffect(() => {
    let intervalId;
    async function fetchValue() {
      // Sayfa görünür değilse veri çekme
      if (!isPageVisible) return;

      try {
        const aggregationResult = await getAggregation(
          widget.request.payload,
          widget.request.endpoint
        );

        if (Array.isArray(aggregationResult) && aggregationResult.length > 0) {
          const numericValue = aggregationResult[0][widget.targetObject];
          const targetObjectCode = parseInt(widget.targetObject);
          const objectInfo = objectData.find(
            (obj) => obj.code === targetObjectCode
          );

          if (objectInfo) {
            if (
              widget.isConvertableObject === false ||
              objectInfo.isConvertableObject === false
            ) {
              setValue(numericValue ?? null);
            } else {
              const convertedValue = ConvertOIDValueToString(
                {
                  Type: objectInfo.type,
                },
                numericValue
              );
              setValue(convertedValue ?? null);
            }
          } else {
            setValue(numericValue ?? null);
          }
        } else {
          setValue(null);
        }
      } catch (err) {
        console.error("Error fetching aggregation for widget:", err);
        setValue(null);
      } finally {
        setLoadingValue(false);
      }
    }

    fetchValue();
    intervalId = setInterval(fetchValue, 30000);

    return () => {
      clearInterval(intervalId);
    };
  }, [widget, objectData, getAggregation, isPageVisible]);

  const formatAsFloat = (val) => {
    if (val === null || val === undefined) return val;
    const num = typeof val === "number" ? val : Number(val);
    if (!Number.isFinite(num)) return val;
    return num.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  return (
    <Box
      sx={(theme) => ({
        p: 1.5,
        borderRadius: 0.5,
        border:
          theme.palette.mode === "dark"
            ? "1px solid #404040"
            : "1px solid #e0e0e0",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        marginBottom: 2,
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: theme.palette.background.paper,
      })}
    >
      <Typography variant="subtitle2" color="text.secondary">
        {t(`widgets.${widget.headKey}`)}
      </Typography>

      {loadingValue ? (
        <CircularProgress size={24} sx={{ my: 1 }} />
      ) : (
        <Typography
          variant="h6"
          sx={{
            my: 0.5,
            textAlign: "center",
            fontSize: { xs: "0.95rem", sm: "1.05rem" },
            lineHeight: 1.15,
            fontWeight: 400,
          }}
          color="text.primary"
        >
          {widget.iconName === "CurrencyLiraIcon" && (
            <CurrencyLiraIcon
              fontSize="small"
              sx={{ verticalAlign: "middle", ml: 0.5 }}
            />
          )}
          {value !== null
            ? widget.suffixKey !== null
              ? `${formatAsFloat(value)} ${t(`widgets.${widget.suffixKey}`)}`
              : formatAsFloat(value)
            : "-"}
        </Typography>
      )}

      <Typography variant="caption" color="text.secondary">
        {t(`widgets.${widget.titleKey}`)}
      </Typography>
    </Box>
  );
}

function WidgetTable({ widget, getAggregation, objectData }) {
  const { t } = useTranslation();
  const [tableData, setTableData] = useState(null);
  const [loadingValue, setLoadingValue] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const isFirstLoadRef = useRef(true);
  const isPageVisible = usePageVisibility();

  useEffect(() => {}, []);

  useEffect(() => {
    let intervalId;
    async function fetchTableData() {
      // Sayfa görünür değilse veri çekme
      if (!isPageVisible) return;

      if (isFirstLoadRef.current) {
        setLoadingValue(true);
      } else {
        setIsRefreshing(true);
      }
      try {
        const authToken = localStorage.getItem("authToken");
        const projectCode = localStorage.getItem("projectCode");

        const endpointParts = widget.request.endpoint.split("api/");
        const apiPath =
          endpointParts.length > 1 ? endpointParts[1] : widget.request.endpoint;

        const fullUrl = `${API_BASE_URL}/${projectCode}/api/${apiPath}`;

        const response = await fetch(fullUrl, {
          method: widget.request.method || "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        if (result && result.data) {
          const nextData = {
            headers: result.data.headers || [],
            rowHeaders: result.data.rowHeaders || [],
            cells: result.data.cells || [],
            title: result.data.title || "",
          };
          setTableData((prev) => {
            try {
              const prevStr = JSON.stringify(prev ?? {});
              const nextStr = JSON.stringify(nextData);
              if (prevStr === nextStr) return prev;
            } catch (_) {}
            return nextData;
          });
        } else {
          setTableData(
            (prev) =>
              prev ?? {
                headers: [],
                rowHeaders: [],
                cells: [],
                title: "",
              }
          );
        }
      } catch (err) {
        console.error("Error fetching table data for widget:", err);
        // İlk yüklemede veri yoksa boş yapı ver; sonrasında mevcut veriyi koru
        if (isFirstLoadRef.current) {
          setTableData({ headers: [], rowHeaders: [], cells: [], title: "" });
        }
      } finally {
        if (isFirstLoadRef.current) {
          setLoadingValue(false);
          isFirstLoadRef.current = false;
        } else {
          setIsRefreshing(false);
        }
      }
    }

    fetchTableData();
    intervalId = setInterval(fetchTableData, 30000);

    return () => {
      clearInterval(intervalId);
    };
  }, [widget, isPageVisible]);

  return (
    <Paper
      elevation={0}
      sx={(theme) => ({
        p: 0,
        borderRadius: 0,
        width: "100%",
        display: "flex",
        flexDirection: "column",
        marginBottom: 2,
        backgroundColor: theme.palette.background.paper,
        boxShadow: "none",
        border:
          theme.palette.mode === "dark"
            ? "1px solid #404040"
            : "1px solid #e0e0e0",
        transition: "all 0.3s ease-in-out",
        position: "relative",
      })}
    >
      <Box
        sx={(theme) => ({
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          backgroundColor:
            theme.palette.mode === "dark" ? "#2d3830" : "#70d470ff",
          color: theme.palette.mode === "dark" ? "#ffffff" : "#ffffff",
          padding: "12px 16px",
        })}
      >
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {tableData?.title === "sumSales"
            ? t("widgets.sumSales", "Toplam Satış")
            : tableData?.title === "salesCount"
            ? t("widgets.salesCount", "Satış Adeti")
            : tableData?.title
            ? t(`widgets.${tableData.title}`, tableData.title)
            : ""}
        </Typography>
        {isRefreshing && (
          <CircularProgress size={16} thickness={6} sx={{ color: "white" }} />
        )}
      </Box>
      <TableContainer>
        <Table
          size="small"
          stickyHeader
          sx={{
            tableLayout: "fixed",
            width: "100%",
          }}
        >
          <TableHead>
            <TableRow sx={{ height: 44 }}>
              <TableCell
                sx={(theme) => {
                  return {
                    fontWeight: 500,
                    backgroundColor:
                      theme.palette.mode === "dark" ? "#2a2a2a" : "#fafafa",
                    color: theme.palette.text.primary,
                    padding: "4px 8px",
                    fontSize: "0.85rem",
                    borderBottom:
                      theme.palette.mode === "dark"
                        ? "2px solid #555555"
                        : "2px solid #c0c0c0",
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                    textAlign: "right",
                    width: `${
                      tableData?.headers?.length
                        ? 100 / (tableData.headers.length + 1)
                        : 20
                    }%`,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  };
                }}
              ></TableCell>
              {tableData?.headers?.map((header, index) => {
                let translatedHeader = header;
                if (header === "card")
                  translatedHeader = t("widgets.card", "Kart");
                else if (header === "cash")
                  translatedHeader = t("widgets.cash", "Nakit");
                else if (header === "app")
                  translatedHeader = t("widgets.app", "Mobil");
                else if (header === "sum")
                  translatedHeader = t("widgets.sum", "Toplam");
                return (
                  <TableCell
                    key={index}
                    sx={(theme) => ({
                      fontWeight: 500,
                      backgroundColor:
                        theme.palette.mode === "dark" ? "#2a2a2a" : "#fafafa",
                      color: theme.palette.text.primary,
                      padding: "4px 8px",
                      textAlign: "center",
                      fontSize: "0.85rem",
                      borderBottom:
                        theme.palette.mode === "dark"
                          ? "2px solid #555555"
                          : "2px solid #c0c0c0",
                      position: "sticky",
                      top: 0,
                      zIndex: 10,
                      width: `${
                        tableData?.headers?.length
                          ? 100 / (tableData.headers.length + 1)
                          : 20
                      }%`,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    })}
                  >
                    {translatedHeader}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {tableData?.cells?.map((row, rowIndex) => (
              <TableRow
                key={rowIndex}
                sx={(theme) => ({
                  height: 44,
                  "&:nth-of-type(even)": {
                    backgroundColor:
                      theme.palette.mode === "dark" ? "#222b31" : "#f5f5f5",
                  },
                  "&:hover": {
                    backgroundColor:
                      theme.palette.mode === "dark" ? "#35596b" : "#e0f2f1",
                  },
                  transition: "background-color 0.2s ease",
                  borderBottom:
                    theme.palette.mode === "dark"
                      ? "1px solid #555555"
                      : "1px solid #c0c0c0",
                })}
              >
                <TableCell
                  sx={(theme) => ({
                    fontWeight: 500,
                    backgroundColor:
                      theme.palette.mode === "dark" ? "#2a2a2a" : "#fafafa",
                    color: theme.palette.text.primary,
                    padding: "4px 8px",
                    textAlign: "center",
                    fontSize: "0.8rem",
                    borderRight:
                      theme.palette.mode === "dark"
                        ? "1px solid #555555"
                        : "1px solid #c0c0c0",
                    width: `${
                      tableData?.headers?.length
                        ? 100 / (tableData.headers.length + 1)
                        : 20
                    }%`,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  })}
                >
                  {(() => {
                    const rowHeader = tableData?.rowHeaders?.[rowIndex];
                    if (!rowHeader) return `${rowIndex + 1}`;
                    switch (rowHeader) {
                      case "today":
                        return t("widgets.today", "Bugün");
                      case "yesterday":
                        return t("widgets.yesterday", "Dün");
                      case "thisWeek":
                        return t("widgets.thisWeek", "Bu Hafta");
                      case "lastWeek":
                        return t("widgets.lastWeek", "Geçen Hafta");
                      case "thisMonth":
                        return t("widgets.thisMonth", "Bu Ay");
                      case "lastMonth":
                        return t("widgets.lastMonth", "Geçen Ay");
                      case "thisYear":
                        return t("widgets.thisYear", "Bu Yıl");
                      case "lastYear":
                        return t("widgets.lastYear", "Geçen Yıl");
                      default:
                        return rowHeader;
                    }
                  })()}
                </TableCell>
                {row.map((cell, cellIndex) => (
                  <TableCell
                    key={cellIndex}
                    sx={(theme) => ({
                      textAlign: "right",
                      padding: "4px 8px",
                      fontSize: "0.8rem",
                      color:
                        cell !== null
                          ? theme.palette.mode === "dark"
                            ? "#e0f7fa"
                            : "#263238"
                          : theme.palette.mode === "dark"
                          ? "#666"
                          : "#b0b8c1",
                      fontWeight: cell !== null ? "600" : "400",
                      borderRight:
                        cellIndex === row.length - 1
                          ? "none"
                          : theme.palette.mode === "dark"
                          ? "1px solid #555555"
                          : "1px solid #c0c0c0",
                      width: `${
                        tableData?.headers?.length
                          ? 100 / (tableData.headers.length + 1)
                          : 20
                      }%`,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    })}
                  >
                    {cell !== null ? (
                      <Typography
                        variant="body1"
                        fontWeight={400}
                        sx={(theme) => ({
                          display: "block",
                          width: "100%",
                          textAlign: "right",
                          color:
                            theme.palette.mode === "dark"
                              ? "#b3e5fc"
                              : "#000000",
                          fontSize: "0.9rem",
                          fontFamily: "'Roboto Mono', monospace",
                          lineHeight: 1.1,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        })}
                      >
                        {typeof cell === "number"
                          ? cell.toLocaleString()
                          : cell}
                      </Typography>
                    ) : (
                      <Typography
                        variant="body1"
                        fontWeight={400}
                        sx={(theme) => ({
                          display: "block",
                          width: "100%",
                          textAlign: "right",
                          color:
                            theme.palette.mode === "dark" ? "#666" : "#bdbdbd",
                          fontSize: "0.9rem",
                          // fontFamily: "'Roboto Mono', monospace",
                          lineHeight: 1.1,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        })}
                      >
                        -
                      </Typography>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {loadingValue && (
        <Box
          sx={(theme) => ({
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(18, 18, 18, 0.6)"
                : "rgba(255, 255, 255, 0.6)",
            backdropFilter: "blur(1.5px)",
            zIndex: 10,
            pointerEvents: "none",
          })}
        >
          <CircularProgress size={32} thickness={4} sx={{ color: "#6b9b76" }} />
        </Box>
      )}
    </Paper>
  );
}

// Yeni WidgetList: targetObjectList -> kolonlar, getAggregation sonucu -> satırlar
function WidgetList({ widget, getAggregation, objectData }) {
  const { t, i18n } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasPaymentType, setHasPaymentType] = useState(false);
  const isPageVisible = usePageVisibility();

  // Grid görünümü: 8 satır görünsün, fazlası içeride scroll edilsin
  const VISIBLE_ROWS = 8;
  const ROW_HEIGHT = 44; // diğer gridlerde de kullanılana yakın sabit
  const HEADER_HEIGHT = 48;

  // Kolonları widget.targetObjectList'ten oluştur
  const columns = useMemo(() => {
    if (!Array.isArray(widget.targetObjectList)) return [];
    // Tarih kolonu (epoch -> date) her zaman ilk sütun (veri yoksa '-')
    const dateColumn = {
      field: "date",
      headerName: t("widgets.date", "Date"),
      flex: 1,
      minWidth: 130,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            width: "100%",
            textAlign: "center",
            fontSize: "0.7rem",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {params.value ?? "-"}
        </Typography>
      ),
    };

    const paymentTypeColumn = {
      field: "paymentType",
      headerName: t("widgets.paymentType", "Ödeme Tipi"),
      flex: 1,
      minWidth: 100,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            width: "100%",
            textAlign: "center",
            fontSize: "0.75rem",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {params.value ?? "-"}
        </Typography>
      ),
    };

    const lang = (
      localStorage.getItem("i18nextLng") ||
      i18n.language ||
      "en"
    ).split("-")[0].toLowerCase(); // tr-TR -> tr
    const dynamicColumns = widget.targetObjectList
      .filter((c) => c !== "NID")
      .map((code) => {
        const field = code.toString();
        let headerName = field;
        const numericCode = parseInt(field, 10);
        if (!Number.isNaN(numericCode)) {
          const objInfo = objectData.find((o) => o.code === numericCode);
          if (objInfo) {
            // Dil -> name alanı eşleme: tr->name1, de->name3, fr->name4, es->name5, diğer->name2
            const localizedName =
              lang === "tr"
                ? objInfo.name1 ?? objInfo.name
                : lang === "de"
                ? objInfo.name3 ?? objInfo.name
                : lang === "fr"
                ? objInfo.name4 ?? objInfo.name
                : lang === "es"
                ? objInfo.name5 ?? objInfo.name
                : objInfo.name2 ?? objInfo.name1 ?? objInfo.name;
            if (localizedName) headerName = localizedName;
            else {
              const translationKey = `widgets.${field}`;
              headerName = t(translationKey, { defaultValue: field });
            }
          } else {
            const translationKey = `widgets.${field}`;
            headerName = t(translationKey, { defaultValue: field });
          }
        } else {
          const translationKey = `widgets.${field}`;
          headerName = t(translationKey, { defaultValue: field });
        }
        return {
          field,
          headerName,
          flex: 1,
          minWidth: 90,
          sortable: false,
          headerAlign: "center",
          align: "center",
          renderCell: (params) => {
            const value = params.value;
            return (
              <Typography
                variant="body2"
                sx={{
                  width: "100%",
                  textAlign: "center",
                  fontSize: "0.75rem",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {typeof value === "number"
                  ? value.toLocaleString()
                  : value ?? "-"}
              </Typography>
            );
          },
        };
      });
    return hasPaymentType
      ? [dateColumn, paymentTypeColumn, ...dynamicColumns]
      : [dateColumn, ...dynamicColumns];
  }, [widget.targetObjectList, t, objectData, i18n.language, hasPaymentType]);

  const convertValue = useMemo(
    () => (codeStr, rawVal) => {
      if (rawVal === null || rawVal === undefined) return null;
      if (codeStr === "NID") return rawVal;
      const numericCode = parseInt(codeStr, 10);
      if (isNaN(numericCode)) return rawVal;
      const objInfo = objectData.find((o) => o.code === numericCode);
      if (!objInfo) return rawVal;
      if (
        widget.isConvertableObject === false ||
        objInfo.isConvertableObject === false
      ) {
        return rawVal;
      }
      try {
        return ConvertOIDValueToString({ Type: objInfo.type }, rawVal);
      } catch (e) {
        return rawVal;
      }
    },
    [objectData, widget.isConvertableObject]
  );

  useEffect(() => {
    let intervalId;
    async function fetchList() {
      // Sayfa görünür değilse veri çekme
      if (!isPageVisible) return;

      setLoading(true);
      setError(null);
      try {
        const data = await getAggregation(
          widget.request.payload,
          widget.request.endpoint
        );
        let records = [];
        if (Array.isArray(data)) {
          records = data;
        } else if (data && Array.isArray(data.rows)) {
          records = data.rows;
        } else if (data && Array.isArray(data.data)) {
          records = data.data;
        } else {
          records = [];
        }
        records.sort((a, b) => {
          const getEpoch = (rec) => {
            let v = rec?.epoch ?? rec?.Epoch ?? rec?.timestamp ?? rec?.time;
            if (v === undefined || v === null || v === "") return -Infinity;
            let num = Number(v);
            if (Number.isNaN(num)) return -Infinity;
            if (num < 1e12) num *= 1000;
            return num;
          };
          return getEpoch(b) - getEpoch(a);
        });
        const getValueFromRecord = (rec, code) => {
          if (!rec || typeof rec !== "object") return null;
          if (Object.prototype.hasOwnProperty.call(rec, code)) {
            return rec[code];
          }
          const numCode = Number(code);
          if (!Number.isNaN(numCode) && Array.isArray(rec.paramList)) {
            const found = rec.paramList.find((p) => Number(p?.oid) === numCode);
            if (found && found.val !== undefined) return found.val;
          }
          return null;
        };

        const coerceNumberIfPossible = (v) => {
          if (v === null || v === undefined) return v;
          if (typeof v === "number") return v;
          if (typeof v === "string" && v.trim() !== "") {
            const n = Number(v);
            if (!Number.isNaN(n)) return n;
          }
          return v;
        };

        const getPaymentTypeName = (val) => {
          const num = Number(val);
          if (num === 307) return t("vendingSales.cash", "Nakit");
          if (num === 309) return t("vendingSales.mobile", "Mobil");
          if (num === 314) return t("vendingSales.card", "Kart");
          return null;
        };

        let sawPaymentType = false;

        const processed = records.map((rec, idx) => {
          const rawEpoch =
            rec?.epoch ?? rec?.Epoch ?? rec?.timestamp ?? rec?.time;
          const id = rec?._id ?? `${rec?.NID ?? "n"}-${rawEpoch ?? "t"}-${idx}`;
          const row = {
            id,
            NID: rec?.NID
          };

          if (rec?.val !== undefined && rec?.val !== null && rec?.val !== "") {
            const name = getPaymentTypeName(rec.val);
            if (name) {
              row.paymentType = name;
              sawPaymentType = true;
            }
          }

          if (rawEpoch !== undefined && rawEpoch !== null && rawEpoch !== "") {
            let epochNum = Number(rawEpoch);
            if (!Number.isNaN(epochNum)) {
              if (epochNum < 1e12) epochNum *= 1000;
              const d = new Date(epochNum);
              const isValid = !isNaN(d.getTime());
              row.date = isValid
                ? d.toLocaleString(undefined, {
                    year: "2-digit",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })
                : "-";
            } else {
              row.date = "-";
            }
          } else {
            row.date = null;
          }

          if (Array.isArray(widget.targetObjectList)) {
            widget.targetObjectList.forEach((code) => {
              if (code === "NID") return;
              const rawVal = getValueFromRecord(rec, code);
              const maybeNumber = coerceNumberIfPossible(rawVal);
              row[code] = convertValue(code, maybeNumber);
            });
          }
          return row;
        });
        setRows(processed);
        setHasPaymentType(sawPaymentType);
      } catch (e) {
        console.error("WidgetList fetch error", e);
        setError(e.message || "Veri alınamadı");
        setRows([]);
      } finally {
        setLoading(false);
      }
    }
    fetchList();
    intervalId = setInterval(fetchList, 30000);
    return () => clearInterval(intervalId);
  }, [widget, getAggregation, objectData, convertValue, isPageVisible]);

  return (
    <Paper
      elevation={2}
      sx={(theme) => ({
        p: 0.5,
        borderRadius: 0,
        width: "100%",
        display: "flex",
        flexDirection: "column",
        marginBottom: 2,
        backgroundColor: theme.palette.background.paper,
        position: "relative",
        border:
          theme.palette.mode === "dark"
            ? "1px solid #404040"
            : "1px solid #e0e0e0",
      })}
    >
      <Typography
        variant="h4"
        align="center"
        sx={{
          px: 1,
          py: 0.5,
          fontWeight: 600,
          textAlign: "center",
          width: "100%",
        }}
        color="text.primary"
      >
        {t(`widgets.${widget.headKey}`, widget.headKey)}
      </Typography>
      <Box
        sx={{
          flex: 1,
          height: HEADER_HEIGHT + ROW_HEIGHT * VISIBLE_ROWS,
          overflowX: "auto",
          width: "100%",
          "& > *": {
            minWidth: { xs: "700px", sm: "100%" },
          },
        }}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          hideFooter
          density="compact"
          loading={loading}
          rowHeight={ROW_HEIGHT}
          headerHeight={HEADER_HEIGHT}
          onRowClick={(params) => {
            const nid = params.row.NID;
            if (nid) {
              window.open(`/vending-machines/${nid}`, '_blank');
            }
          }}
          sx={{
            height: "100%",
            overflow: "unset",
            border: "none",
            "& .MuiDataGrid-main": {
              height: "100%",
              overflowX: "auto",
              minWidth: { xs: "700px", sm: "100%" },
            },
            "& .MuiDataGrid-virtualScroller": {
              height: "100%",
              overflowY: "auto",
              overflowX: "auto",
            },
            "& .MuiDataGrid-columnHeaders": {
              color: (theme) => theme.palette.text.primary,
              minHeight: HEADER_HEIGHT,
              maxHeight: HEADER_HEIGHT,
            },
            "& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell": {
              padding: "0 4px",
            },
            "& .MuiDataGrid-cell": {
              borderBottom: (theme) =>
                `1px solid ${
                  theme.palette.mode === "dark" ? "#29434e" : "#e0e0e0"
                }`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              lineHeight: 1.1,
            },
            "& .MuiDataGrid-row": {
              alignItems: "center !important",
              cursor: "pointer",
              "&:hover": {
                backgroundColor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.08)"
                    : "rgba(0, 0, 0, 0.04)",
              },
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              width: "100%",
              textAlign: "center",
              lineHeight: 1.2,
            },
          }}
          getRowId={(row) => row.id}
        />
        {error && !loading && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              backgroundColor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(0,0,0,0.4)"
                  : "rgba(255,255,255,0.6)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 14,
              fontWeight: 500,
              color: "error.main",
            }}
          >
            {error}
          </Box>
        )}
      </Box>
    </Paper>
  );
}

export default function ObjectDetails() {
  const [widgetData, setWidgetData] = useState(null);
  const [loading, setLoading] = useState(true);

  const objectData = useObjectData();

  useEffect(() => {
    async function fetchWidgets() {
      setLoading(true);
      try {
        const projectCode = localStorage.getItem("projectCode");
        const projectData = await getProjects();
        const currentProject = projectData.find(
          (project) => project.code === projectCode
        );

        if (currentProject && currentProject.projectDashboardWidgetJson) {
          const widgets = JSON.parse(currentProject.projectDashboardWidgetJson);
          setWidgetData(widgets);
        } else {
          console.error("No widgets found for the current project.");
          setWidgetData({ widgets: [] });
        }
      } catch (error) {
        console.error("Error fetching widgets:", error);
        setWidgetData({ widgets: [] });
      } finally {
        setLoading(false);
      }
    }
    fetchWidgets();
  }, []);

  const hasWidgets =
    widgetData &&
    Array.isArray(widgetData.widgets) &&
    widgetData.widgets.length > 0;

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      {hasWidgets && (
        <Box>
          {widgetData.widgets.map((row, rowIndex) => (
            <Grid container spacing={2} key={`row-${rowIndex}`}>
              {row.map((widget, colIndex) => (
                <Grid
                  item
                  xs={12}
                  sm={12 / row.length}
                  key={`widget-${rowIndex}-${colIndex}`}
                >
                  {widget.widgetType === "box" && (
                    <WidgetBox
                      widget={widget}
                      getAggregation={getAggregation}
                      objectData={objectData}
                    />
                  )}
                  {widget.widgetType === "table" && (
                    <WidgetTable
                      widget={widget}
                      getAggregation={getAggregation}
                      objectData={objectData}
                    />
                  )}
                  {widget.widgetType === "list" && (
                    <WidgetList
                      widget={widget}
                      getAggregation={getAggregation}
                      objectData={objectData}
                    />
                  )}
                </Grid>
              ))}
            </Grid>
          ))}
        </Box>
      )}
    </>
  );
}
